/**
 * Assign Jabatan Dialog Component
 * 
 * Dialog for assigning jabatan to users.
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Briefcase, Search, User, AlertCircle, CheckCircle2, Info, GripVertical } from "lucide-react";
import { userService, type Member } from "@/services/userService";
import { managementService, type Jabatan, type JabatanLevel } from "@/services/managementService";
import { LocationSelect } from "@/components/ui/LocationSelect";
import { locationService } from "@/services/locationService";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface AssignJabatanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AssignJabatanDialog({ open, onOpenChange, onSuccess }: AssignJabatanDialogProps) {
  const [callsign, setCallsign] = React.useState("");
  const [selectedUser, setSelectedUser] = React.useState<Member | null>(null);
  const [selectedJabatan, setSelectedJabatan] = React.useState<string>("");
  const [jabatanList, setJabatanList] = React.useState<Jabatan[]>([]);
  const [province, setProvince] = React.useState<string>("");
  const [city, setCity] = React.useState<string>("");
  const [startDate, setStartDate] = React.useState<string>("");
  const [endDate, setEndDate] = React.useState<string>("");
  const [notes, setNotes] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [searching, setSearching] = React.useState(false);
  const [loadingJabatan, setLoadingJabatan] = React.useState(false);
  const [loadingProvinces, setLoadingProvinces] = React.useState(false);
  const [provinces, setProvinces] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  // Dragging state
  const [isDragging, setIsDragging] = React.useState(false);
  const [position, setPosition] = React.useState<{ x: number; y: number } | null>(null);
  const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);

  // Load jabatan list
  React.useEffect(() => {
    if (open) {
      const loadJabatan = async () => {
        setLoadingJabatan(true);
        setError(null);
        try {
          const list = await managementService.getAllJabatan();
          setJabatanList(list);
          if (list.length === 0) {
            setError("Tidak ada jabatan tersedia. Silakan buat jabatan terlebih dahulu.");
          }
        } catch (err) {
          console.error("Error loading jabatan:", err);
          setError(err instanceof Error ? err.message : "Gagal memuat daftar jabatan");
        } finally {
          setLoadingJabatan(false);
        }
      };
      loadJabatan();
    }
  }, [open]);

  // Get selected jabatan data
  const selectedJabatanData = React.useMemo(() => {
    return jabatanList.find((j) => j._id === selectedJabatan);
  }, [jabatanList, selectedJabatan]);

  // Load provinces when province-level jabatan is selected
  React.useEffect(() => {
    if (open && selectedJabatanData?.level === "province" && provinces.length === 0) {
      const loadProvinces = async () => {
        setLoadingProvinces(true);
        try {
          const data = await locationService.fetchProvinces();
          setProvinces(data);
        } catch (err) {
          console.error("Error loading provinces:", err);
        } finally {
          setLoadingProvinces(false);
        }
      };
      loadProvinces();
    }
  }, [open, selectedJabatanData?.level, provinces.length]);

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setCallsign("");
      setSelectedUser(null);
      setSelectedJabatan("");
      setProvince("");
      setCity("");
      setStartDate("");
      setEndDate("");
      setNotes("");
      setError(null);
      setPosition(null);
    }
  }, [open]);

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!headerRef.current || !dialogRef.current) return;
    
    e.preventDefault();
    
    const rect = dialogRef.current.getBoundingClientRect();
    const currentPosition = position || {
      x: rect.left,
      y: rect.top,
    };
    
    setDragOffset({
      x: e.clientX - currentPosition.x,
      y: e.clientY - currentPosition.y,
    });
    
    if (!position) {
      setPosition({
        x: rect.left,
        y: rect.top,
      });
    }
    
    setIsDragging(true);
  };

  // Handle drag
  React.useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dialogRef.current) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      const maxX = window.innerWidth - dialogRef.current.offsetWidth;
      const maxY = window.innerHeight - dialogRef.current.offsetHeight;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset, position]);

  // Search user by callsign
  const handleSearchUser = async () => {
    if (!callsign.trim()) {
      setError("Callsign harus diisi");
      return;
    }

    setSearching(true);
    setError(null);
    setSelectedUser(null);

    try {
      const result = await userService.fetchMembers({
        search: callsign.trim().toUpperCase(),
        limit: 1,
      });

      if (result.data.length > 0) {
        const user = result.data[0];
        if (user) {
          setSelectedUser(user);
          setProvince(user.provinsi || "");
          setCity(user.kabupaten || "");
          toast.success("User ditemukan", {
            description: `${user.nama_lengkap} (${user.callsign})`,
          });
        } else {
          setError("User tidak ditemukan");
          setSelectedUser(null);
        }
      } else {
        setError("User tidak ditemukan");
        setSelectedUser(null);
      }
    } catch (err) {
      console.error("Error searching user:", err);
      setError(err instanceof Error ? err.message : "Gagal mencari user");
      setSelectedUser(null);
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!selectedUser) {
        setError("User harus dipilih");
        setLoading(false);
        return;
      }

      if (!selectedJabatan) {
        setError("Jabatan harus dipilih");
        setLoading(false);
        return;
      }

      await managementService.createAssignment({
        user: selectedUser._id,
        jabatan: selectedJabatan,
        province: province || undefined,
        city: city || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        isActive: true,
        notes: notes || undefined,
      });

      toast.success("Penugasan berhasil dibuat", {
        description: `Jabatan berhasil ditugaskan ke ${selectedUser.nama_lengkap}`,
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Gagal membuat penugasan");
    } finally {
      setLoading(false);
    }
  };

  const getLevelConfig = (level?: JabatanLevel) => {
    if (!level) return { label: "", color: "", bgColor: "", borderColor: "" };
    switch (level) {
      case "nasional":
        return {
          label: "Nasional",
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-950/20",
          borderColor: "border-blue-200 dark:border-blue-800",
        };
      case "province":
        return {
          label: "Provinsi",
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-950/20",
          borderColor: "border-green-200 dark:border-green-800",
        };
      case "city":
        return {
          label: "Kota/Kabupaten",
          color: "text-purple-600 dark:text-purple-400",
          bgColor: "bg-purple-50 dark:bg-purple-950/20",
          borderColor: "border-purple-200 dark:border-purple-800",
        };
    }
  };

  const levelConfig = selectedJabatanData ? getLevelConfig(selectedJabatanData.level) : null;
  const isFormValid = selectedUser && selectedJabatan;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={dialogRef}
        className={cn(
          "sm:max-w-[650px] max-h-[90vh] overflow-y-auto",
          isDragging && "cursor-grabbing",
          position && "translate-x-0 translate-y-0"
        )}
        style={
          position
            ? {
                left: `${position.x}px`,
                top: `${position.y}px`,
                transform: "none",
              }
            : undefined
        }
      >
        <DialogHeader className="space-y-3 pb-4 border-b">
          <div
            ref={headerRef}
            className={cn(
              "flex items-center gap-3 cursor-grab select-none",
              isDragging && "cursor-grabbing"
            )}
            onMouseDown={handleMouseDown}
          >
            <div className={cn(
              "p-2 rounded-lg",
              levelConfig?.bgColor || "bg-muted",
              levelConfig?.borderColor || "border-muted",
              "border"
            )}>
              <Briefcase className={cn("h-5 w-5", levelConfig?.color || "text-muted-foreground")} />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                Assign Jabatan
                <GripVertical className="h-4 w-4 text-muted-foreground opacity-50" />
              </DialogTitle>
              <DialogDescription className="text-sm mt-1">
                Tugaskan jabatan kepada user dalam sistem
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-5">
            {/* User Search */}
            <div className="space-y-2">
              <Label htmlFor="callsign" className="text-sm font-semibold flex items-center gap-2">
                Cari User
                <span className="text-red-500">*</span>
                {selectedUser && (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                )}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="callsign"
                  value={callsign}
                  onChange={(e) => setCallsign(e.target.value.toUpperCase())}
                  placeholder="Masukkan callsign (contoh: YB8MR)"
                  disabled={loading || searching}
                  className="h-11 text-base font-mono"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearchUser();
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={handleSearchUser}
                  disabled={loading || searching || !callsign.trim()}
                  className="h-11"
                >
                  {searching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {selectedUser && (
                <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{selectedUser.nama_lengkap}</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedUser.callsign} - {selectedUser.provinsi}, {selectedUser.kabupaten}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Jabatan Selection */}
            <div className="space-y-2">
              <Label htmlFor="jabatan" className="text-sm font-semibold flex items-center gap-2">
                Jabatan
                <span className="text-red-500">*</span>
                {selectedJabatan && (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                )}
              </Label>
              {loadingJabatan ? (
                <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/50">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Memuat daftar jabatan...</span>
                </div>
              ) : (
                <>
              <Select
                value={selectedJabatan}
                onValueChange={(value) => {
                  setSelectedJabatan(value);
                  // Clear location when nasional jabatan is selected
                  // Clear city when province-level jabatan is selected
                  const jabatan = jabatanList.find((j) => j._id === value);
                  if (jabatan?.level === "nasional") {
                    setProvince("");
                    setCity("");
                  } else if (jabatan?.level === "province") {
                    // Clear city for province-level jabatan
                    setCity("");
                  }
                }}
                disabled={loading || loadingJabatan || jabatanList.length === 0}
              >
                    <SelectTrigger id="jabatan" className="h-11 text-base">
                      <SelectValue placeholder={jabatanList.length === 0 ? "Tidak ada jabatan" : "Pilih jabatan"} />
                    </SelectTrigger>
                    <SelectContent>
                      {jabatanList.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          Tidak ada jabatan tersedia
                        </div>
                      ) : (
                        jabatanList.map((jabatan) => (
                          <SelectItem key={jabatan._id} value={jabatan._id}>
                            {jabatan.name} ({jabatan.level})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {jabatanList.length === 0 && !loadingJabatan && (
                    <p className="text-xs text-muted-foreground">
                      Silakan buat jabatan terlebih dahulu di tab "Jabatan"
                    </p>
                  )}
                  {selectedJabatanData && (
                    <div className={cn(
                      "p-3 rounded-lg border flex items-center gap-3",
                      levelConfig?.bgColor,
                      levelConfig?.borderColor
                    )}>
                      <Briefcase className={cn("h-5 w-5 shrink-0", levelConfig?.color)} />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{selectedJabatanData.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {levelConfig?.label} - {selectedJabatanData.description || "Tidak ada deskripsi"}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Province and City - Show based on jabatan level */}
            {selectedJabatanData && selectedJabatanData.level !== "nasional" && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">
                  Lokasi
                  <span className="text-muted-foreground font-normal ml-2">(Opsional)</span>
                </Label>
                {selectedJabatanData.level === "province" ? (
                  // Only show province for province-level jabatan
                  <div className="space-y-2">
                    <Select
                      value={province || ""}
                      onValueChange={(value) => {
                        setProvince(value === "" ? "" : value);
                        // Clear city when province is selected for province-level jabatan
                        setCity("");
                      }}
                      disabled={loading || loadingProvinces}
                    >
                      <SelectTrigger className="h-11 text-base">
                        {loadingProvinces ? (
                          <div className="flex items-center">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            <span>Memuat provinsi...</span>
                          </div>
                        ) : (
                          <SelectValue placeholder="Pilih Provinsi" />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {provinces.length === 0 && !loadingProvinces ? (
                          <SelectItem value="no-data" disabled>
                            Tidak ada provinsi
                          </SelectItem>
                        ) : (
                          provinces.map((p) => (
                            <SelectItem key={p} value={p}>
                              {p}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  // Show both province and city for city-level jabatan
                  <LocationSelect
                    province={province || undefined}
                    city={city || undefined}
                    onProvinceChange={(newProvince) => {
                      setProvince(newProvince || "");
                      // Clear city when province changes
                      if (newProvince !== province) {
                        setCity("");
                      }
                    }}
                    onCityChange={(newCity) => {
                      setCity(newCity || "");
                    }}
                    disabled={loading}
                    showLabels={false}
                  />
                )}
              </div>
            )}

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate" className="text-sm font-semibold">
                  Tanggal Mulai
                  <span className="text-muted-foreground font-normal ml-2">(Opsional)</span>
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={loading}
                  className="h-11 text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate" className="text-sm font-semibold">
                  Tanggal Selesai
                  <span className="text-muted-foreground font-normal ml-2">(Opsional)</span>
                </Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={loading}
                  className="h-11 text-base"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-semibold">
                Catatan
                <span className="text-muted-foreground font-normal ml-2">(Opsional)</span>
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Catatan tambahan..."
                rows={3}
                disabled={loading}
                className="resize-none text-base"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="h-11"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={loading || !isFormValid}
              className="h-11 min-w-[120px]"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Menyimpan...
                </>
              ) : (
                <>
                  <Briefcase className="mr-2 h-4 w-4" />
                  Assign Jabatan
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

